/**
 * Порядковые номера товаров внутри листовой категории.
 *
 * Номер устойчив: деактивация его сохраняет, удаление — перенумеровывает
 * оставшиеся товары 1..N. Чистые функции, чтобы одинаково считать на кассе
 * и в тестах.
 */

export interface OrdinalRow {
  id?: number
  categoryParent?: string
  category?: string
  categoryOrdinal?: number
  isDeleted?: boolean
}

/** Ключ группы: верхний уровень + лист. */
export function categoryKey(row: OrdinalRow): string {
  const parent = row.categoryParent?.trim() || row.category?.trim() || 'Общее'
  const leaf = row.category?.trim() || 'Общее'
  return `${parent}\u0000${leaf}`
}

/**
 * Назначить номера 1..N в каждой категории.
 * Порядок: существующий ordinal, затем id (порядок импорта).
 * Мягко удалённые строки не участвуют.
 */
export function assignOrdinals<T extends OrdinalRow>(
  rows: T[]
): Map<number, number> {
  const groups = new Map<string, T[]>()
  for (const row of rows) {
    if (row.isDeleted) continue
    const key = categoryKey(row)
    const list = groups.get(key) ?? []
    list.push(row)
    groups.set(key, list)
  }

  const result = new Map<number, number>()
  for (const list of groups.values()) {
    list.sort((a, b) => {
      const ao = a.categoryOrdinal ?? Number.MAX_SAFE_INTEGER
      const bo = b.categoryOrdinal ?? Number.MAX_SAFE_INTEGER
      if (ao !== bo) return ao - bo
      return (a.id ?? 0) - (b.id ?? 0)
    })
    list.forEach((row, index) => {
      if (row.id != null) result.set(row.id, index + 1)
    })
  }
  return result
}

/** Следующий номер для нового товара в категории. */
export function nextOrdinal(rows: OrdinalRow[]): number {
  let max = 0
  for (const row of rows) {
    if (row.isDeleted) continue
    if (row.categoryOrdinal != null && row.categoryOrdinal > max) {
      max = row.categoryOrdinal
    }
  }
  return max + 1
}
